import { useState } from "react";
import { AppShell } from "@/components/layout/AppShell";
import { BrandButton } from "@/components/kit/BrandButton";
import { BundleNote } from "@/components/kit/BundleNote";
import { GuideSheet } from "@/components/kit/GuideSheet";
import { LockedFeatureCard } from "@/components/kit/LockedFeatureCard";
import { MealProgress } from "@/components/kit/MealProgress";
import { MealTile } from "@/components/kit/MealTile";
import { ProgressRing } from "@/components/kit/ProgressRing";
import { RecipeModal } from "@/components/kit/RecipeModal";
import { SectionHeading } from "@/components/kit/SectionHeading";
import { SurfaceCard } from "@/components/kit/SurfaceCard";
import { WeekIndex } from "@/components/kit/WeekIndex";
import { SLOT_LABELS } from "@/lib/ppcp";
import { PAGES } from "@/constants/testIds";

const swatches = [
  ["Deep Teal", "#004040", "bg-brand-teal"],
  ["Burnt Orange", "#DD4115", "bg-brand-orange"],
  ["Orange Ink (AA)", "#B8330F", "bg-brand-orangeInk"],
  ["Warm Cream", "#FEFAF3", "bg-brand-cream"],
  ["Border", "#ECE5DA", "bg-border"],
];

const meals = [
  {
    slot: "breakfast",
    title: "Moong Dal Chilla",
    description: "Savoury pancakes from soaked yellow moong dal, served with mint chutney.",
    image: "https://images.unsplash.com/photo-1512621776951-a57141f2eefd?crop=entropy&cs=srgb&fm=jpg&q=80&w=600",
    recipe_id: 9001,
    prep_time: "25 mins",
    calories: "212 kcal",
    protein: "14 g",
  },
  {
    slot: "mid_morning",
    title: "Seasonal Fruit Bowl",
    description: "Whatever is in season, cut fresh.",
    image: "https://images.unsplash.com/photo-1547592180-85f173990554?crop=entropy&cs=srgb&fm=jpg&q=80&w=600",
    prep_time: "5 mins",
    calories: "90 kcal",
    protein: "1 g",
    required: false,
  },
];

const demoRecipe = {
  title: "Moong Dal Chilla with Mint Chutney",
  summary: "A protein-rich savoury pancake made from soaked yellow moong dal.",
  image: "https://images.unsplash.com/photo-1512621776951-a57141f2eefd?crop=entropy&cs=srgb&fm=jpg&q=80&w=900",
  servings: "2",
  prep_time: "15 mins",
  cook_time: "10 mins",
  ingredients: [
    { amount: "1", unit: "cup", name: "yellow moong dal", notes: "soaked 4 hours" },
    { amount: "1", unit: "tsp", name: "cumin seeds" },
  ],
  steps: ["Grind the soaked dal to a smooth batter.", "Cook on a hot tawa till golden on both sides."],
  nutrition: { calories: "212 kcal", protein: "14 g" },
};

/** Living style guide. */
export default function KitPreview() {
  const [open, setOpen] = useState(false);
  const [eaten, setEaten] = useState(["breakfast"]);

  return (
    <AppShell title="Design System" subtitle="Component kit" showBack={false}>
      <div data-testid={PAGES.kit} className="contents">
        <header className="space-y-3 pb-2 pt-4">
          <p className="ps-eyebrow">Plattershare</p>
          <h1 className="font-display text-4xl font-semibold leading-tight text-brand-teal sm:text-5xl lg:text-6xl">
            Calm, warm, food-first
          </h1>
          <p className="max-w-xl text-[15px] leading-relaxed text-muted-foreground md:text-base">
            Fraunces for display, Karla for body. Cream ground, floating white cards, deep teal ink,
            burnt orange reserved for action.
          </p>
        </header>

        <SurfaceCard className="space-y-5">
          <SectionHeading eyebrow="Tokens" title="Palette" note="Orange Ink is the AA-safe orange for small text." />
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-5">
            {swatches.map(([name, value, cls]) => (
              <div key={name} className="overflow-hidden rounded-[18px] border border-border">
                <div className={`h-20 ${cls}`} />
                <div className="p-3.5">
                  <p className="text-xs font-bold text-brand-teal">{name}</p>
                  <p className="text-[11px] text-muted-foreground">{value}</p>
                </div>
              </div>
            ))}
          </div>
        </SurfaceCard>

        <SurfaceCard className="space-y-5">
          <SectionHeading eyebrow="Kit" title="Buttons" />
          <div className="flex flex-wrap items-center gap-3">
            <BrandButton name="kit-primary">Primary action</BrandButton>
            <BrandButton name="kit-teal" variant="teal">Teal</BrandButton>
            <BrandButton name="kit-outline" variant="outline">Outline</BrandButton>
            <BrandButton name="kit-ghost" variant="ghost">Ghost</BrandButton>
            <BrandButton name="kit-disabled" disabled>Disabled</BrandButton>
          </div>
        </SurfaceCard>

        <div className="grid gap-6 sm:gap-8 lg:grid-cols-2">
          <SurfaceCard className="space-y-6">
            <SectionHeading eyebrow="Kit" title="Progress" note="Counts alongside percentage." />
            <MealProgress done={3} total={5} />
            <div className="flex items-center gap-6">
              <ProgressRing value={0} />
              <ProgressRing value={40} />
              <ProgressRing value={100} />
            </div>
          </SurfaceCard>

          <SurfaceCard className="space-y-5">
            <SectionHeading eyebrow="Kit" title="Contextual Complete Meal Plan notes" />
            <BundleNote kind="protein" text="Add a bowl of curd to push this past 20 g of protein." />
            <BundleNote kind="tip" text="Drink a glass of water before every meal." />
            <BundleNote kind="grocery" locked upgradeUrl="#" />
          </SurfaceCard>
        </div>

        <SurfaceCard className="space-y-5">
          <SectionHeading eyebrow="Kit" title="Week-based Day Index" />
          <WeekIndex total={21} current={9} completed={[1, 2, 3, 4, 5, 6, 7, 8]} />
        </SurfaceCard>

        <SurfaceCard pad={false} className="overflow-hidden">
          <header className="flex items-baseline justify-between px-6 pb-5 pt-6 sm:px-8 sm:pt-8">
            <h2 className="font-display text-xl font-semibold text-brand-teal sm:text-2xl">Today's Meals</h2>
            <span className="text-xs font-medium text-muted-foreground">{meals.length} meals</span>
          </header>
          <div className="ps-divider" />
          {meals.map((meal, i) => (
            <div key={meal.slot}>
              {i > 0 && <div className="ps-divider" />}
              <div className="px-6 py-6 sm:px-8">
                <div className="mb-3 flex items-center gap-2.5">
                  <p className="ps-eyebrow">{SLOT_LABELS[meal.slot]}</p>
                  {meal.required === false && (
                    <span className="rounded-full bg-secondary px-2.5 py-0.5 text-[10px] font-semibold text-brand-tealSoft">
                      Optional
                    </span>
                  )}
                </div>
                <MealTile
                  meal={meal}
                  completed={eaten.includes(meal.slot)}
                  onToggle={() =>
                    setEaten((p) => (p.includes(meal.slot) ? p.filter((s) => s !== meal.slot) : [...p, meal.slot]))
                  }
                  onOpenRecipe={() => setOpen(true)}
                />
              </div>
            </div>
          ))}
        </SurfaceCard>

        <SurfaceCard className="space-y-5">
          <SectionHeading eyebrow="Kit" title="Locked Complete Meal Plan features" />
          <div className="grid gap-3 sm:grid-cols-2">
            <LockedFeatureCard
              featureKey="protein_engine"
              title="Protein Engine"
              description="Daily protein targets matched to your plan."
              upgradeHref="#"
            />
            <LockedFeatureCard
              featureKey="grocery_engine"
              title="Grocery Guide"
              description="Your shopping guide for the week ahead."
              upgradeHref="#"
            />
          </div>
        </SurfaceCard>

        <SurfaceCard>
          <GuideSheet
            title="Content blocks"
            blocks={[
              { type: "paragraph", text: "Every guide and intro page renders from generic blocks." },
              { type: "list", items: ["Soak dals overnight", "Keep cut vegetables ready", "Cook grains in batches"] },
              { type: "callout", text: "Complete Meal Plan members also get the Protein Guide." },
              { type: "table", columns: ["Item", "Quantity"], rows: [["Moong dal", "500 g"], ["Spinach", "2 bunches"]] },
              { type: "quote", text: "Consistency beats perfection, every single time." },
            ]}
          />
        </SurfaceCard>
      </div>

      <RecipeModal open={open} onOpenChange={setOpen} recipe={demoRecipe} />
    </AppShell>
  );
}
